import { IonPage } from '@ionic/react'
import { useParams } from 'react-router-dom'

import {
  QUERY_KEY_GET_MY_MONITORING_ACCOUNT,
  useGetMyMonitoringAccount,
  useGetMyMonitoringAccountUnits,
  useMigrateMyMonitoringAccount,
  useSendMyMonitoringAccountEmail
} from '@apps-common/apis/portal/my-monitoring'
import {
  MyMonitoringAccountDetailsCard,
  MyMonitoringAccountInternalUnits
} from '@apps-common/apps/portal/pages/my-monitoring/pages/details/components'
import { PullRefresh } from '@apps-common/components/common'
import { PageTitle } from '@apps-common/components/common/portal-title'
import { ProfileUserInitials } from '@apps-common/components/common/user'
import { ButtonRoundedWithIcon } from '@apps-common/components/elements'
import { Header, LayoutContainer, LayoutPadding, VertSpacerSm } from '@apps-common/components/layout'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { Breadcrumbs, Navbar } from '@apps-common/components/navigation'
import { commonIcons } from '@apps-common/config/icons'
import { MyMonitoringAccountRequiredAction } from '@apps-common/config/my-monitoring'
import { toLocalTimestampStr } from '@apps-common/helpers/to-local-timestamp'
import { queryClient } from '@apps-common/lib'
import { ROUTE_MY_MONITORING_ACCOUNT_DETAILS } from '@apps-radionet-portal-root/src/config/private-routes'

export const MyMonitoringAccountDetailsPage = () => {
  const { accountId } = useParams<{ accountId: string }>()
  const id = parseInt(accountId, 10)

  const {
    isLoading: accountIsLoading,
    isError: accountIsError,
    isFetching: accountIsFetching,
    isRefetching: accountIsRefetching,
    refetch: accountRefetch,
    data: accountData
  } = useGetMyMonitoringAccount({ id })

  const {
    isLoading: unitsIsLoading,
    isError: unitsIsError,
    isFetching: unitsIsFetching,
    isRefetching: unitsIsRefetching,
    refetch: unitsRefetch,
    data: unitsData
  } = useGetMyMonitoringAccountUnits({ id })

  const { isPending: migrationPending, mutate: migrateAccount } = useMigrateMyMonitoringAccount(true, () => {
    queryClient.refetchQueries({ queryKey: QUERY_KEY_GET_MY_MONITORING_ACCOUNT(id) })
  })

  const { isPending: sendEmailPending, mutate: sendEmail } = useSendMyMonitoringAccountEmail(true)

  const loading =
    accountIsLoading ||
    accountIsError ||
    accountIsFetching ||
    !accountData ||
    unitsIsLoading ||
    unitsIsError ||
    unitsIsFetching ||
    !unitsData
  const updating = migrationPending || sendEmailPending
  const refetching = accountIsRefetching || unitsIsRefetching

  return (
    <IonPage>
      <Header>
        <div className='sm:w-auto sm:max-w-[750px]'>
          <Navbar showBack transparent icons={[]} />
          <Breadcrumbs route={ROUTE_MY_MONITORING_ACCOUNT_DETAILS} />
          <div className='flex flex-col sm:flex-row item-end justify-between gap-[15px] sm:gap-[10px] '>
            <PageTitle classes='flex items-center justify-start gap-[10px] grow'>
              {loading && (
                <>
                  <div className='bg-skeleton min-w-[40px] min-h-[40px] h-[40px] w-[40px] sm:min-w-[48px] sm:min-h-[48px] sm:h-[48px] sm:w-[48px] rounded-full animate-pulse' />
                  <div className='bg-skeleton h-[40px] sm:h-[42px] grow sm:max-w-[300px] rounded-xl animate-pulse' />
                </>
              )}
              {!loading && (
                <>
                  <ProfileUserInitials
                    classes='sm:w-[40px] sm:h-[40px] mt-[5px]'
                    textClasses='sm:!text-[0.9rem]'
                    name={accountData?.fullName}
                  />
                  <div className='grow text-ellipsis max-w-[85%] whitespace-nowrap overflow-hidden text-[0.9em]'>{accountData?.email}</div>
                </>
              )}
            </PageTitle>
          </div>
          <VertSpacerSm />
          <div className='flex items-end justify-between'>
            {loading && (
              <div className='animate-pulse'>
                <div className='h-[17px] w-[48px] bg-skeleton rounded mb-1' />
                <div className='h-[20px] w-[80px] bg-skeleton rounded' />
              </div>
            )}
            {!loading && (
              <div>
                <div className='jost text-[0.75em] text-text-muted'>Last login</div>
                <div className='text-[0.85rem]'>
                  {accountData?.lastLogin ? toLocalTimestampStr(accountData?.lastLogin, 'Do MMM YYYY hh:mm:ss') : 'Never'}
                </div>
              </div>
            )}
            {accountData && (
              <ModalButton
                component={ConfirmModal}
                disabled={loading || !accountData.migrated || updating}
                componentProps={
                  !accountData?.lastLogin
                    ? {
                        text: 'Registration Email',
                        description: 'Would you like to re-send registration email?'
                      }
                    : {
                        text: 'Forgot Password Email',
                        description: 'Would you like to send forgot password email?'
                      }
                }
                confirmCallback={() => sendEmail({ model: accountData })}
                backdropDismiss
                tooltip={
                  !accountData?.lastLogin
                    ? { id: 'my-monitoring-account-send-registration', message: 'Send Registration Email', place: 'bottom' }
                    : { id: 'my-monitoring-account-send-password-reset', message: 'Send Password Reset', place: 'bottom' }
                }
              >
                <ButtonRoundedWithIcon
                  type='div'
                  text={!accountData?.lastLogin ? 'Re-send Registration' : 'Send Password Reset'}
                  icon={<commonIcons.sendEmail />}
                  className='action-brand'
                  iconContainerClassName='bg-brand text-on-brand'
                />
              </ModalButton>
            )}
          </div>
        </div>
      </Header>
      <LayoutContainer>
        <LayoutPadding topPadding='pt-[60px] sm:pt-[60px]'>
          <PullRefresh
            loading={refetching}
            callback={() => {
              accountRefetch()
              unitsRefetch()
            }}
          />
          <div className='max-w-[750px] flex flex-col gap-[20px]'>
            <div className='max-w-[450px]'>
              {loading && <div className='bg-skeleton border border-skeleton-border rounded-3xl min-h-[57px]' />}
              {!loading && accountData?.requiredAction !== MyMonitoringAccountRequiredAction.migration && (
                <div className='bg-success/50 text-on-success border border-success rounded-3xl flex items-center justify-between px-4 py-2'>
                  <commonIcons.checkCircle className='text-[1.55rem]' />
                  <div className='flex flex-col gap-[1px] text-right'>
                    <div className='text-[0.75rem] jost-medium'>No action required</div>
                    <div className='text-[0.85rem]'>
                      Account status <span className='jost-bold'>Normal</span>
                    </div>
                  </div>
                </div>
              )}
              {!loading && accountData?.requiredAction === MyMonitoringAccountRequiredAction.migration && (
                <div className='bg-warning/50 text-on-warning border border-warning rounded-3xl flex items-center justify-between p-2 py-1'>
                  <div className=' px-2 py-1 rounded-2xl flex items-center justify-center gap-[10px] pr-[10px]'>
                    <commonIcons.toastWarning className='text-[1.5rem]' />
                    <div className='flex flex-col gap-[1px] text-left'>
                      <div className='text-[0.75rem] jost-medium'>Action required</div>
                      <div className='text-[0.85rem]'>Migrate to new system</div>
                    </div>
                  </div>
                  <ModalButton
                    component={ConfirmModal}
                    disabled={updating}
                    componentProps={{
                      text: 'Account Migration',
                      description: 'Would you like to migrate this account to the new system now?',
                      action: ConfirmModalAction.Warning
                    }}
                    confirmCallback={() => migrateAccount({ model: accountData })}
                    backdropDismiss
                  >
                    <ButtonRoundedWithIcon
                      type='div'
                      text='Migrate now'
                      icon={<commonIcons.arrowAngled />}
                      className='action-warning !grow !text-[0.8rem]'
                      iconContainerClassName='bg-warning text-on-warning'
                    />
                  </ModalButton>
                </div>
              )}
            </div>
            <MyMonitoringAccountDetailsCard loading={loading} disabled={migrationPending} data={accountData} />
            <MyMonitoringAccountInternalUnits userId={id} loading={loading} disabled={migrationPending} units={unitsData || []} />
          </div>
        </LayoutPadding>
      </LayoutContainer>
    </IonPage>
  )
}
